import { useEffect, useState, useCallback, useMemo } from "react";
import { Globe2, CalendarClock, Building2 } from "lucide-react";
import { Label } from "../../components/ui/label";
import { crmApi, logDevError } from "../../lib/crm";
import {
  getZonedParts,
  availabilityFromHour,
  offsetFromAmsterdam,
  findMeetingSlots,
  formatTimeInTz,
} from "../../lib/timezones";
import { PlannerResult } from "./TimezoneManager";

/**
 * Read-only Timezone Manager for sales agents.
 * Shows the agent's own tz + assigned lead zones + meeting planner restricted
 * to those assigned zones.
 */
export default function TimezoneAgentView() {
  const [data, setData] = useState(null);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      const r = await crmApi.get("/timezones/my-view");
      setData(r.data);
    } catch (err) {
      logDevError("TimezoneAgentView.fetch", err);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (!data) return <div className="p-8 text-white/40 text-sm">Cargando…</div>;

  const amsParts = getZonedParts(data.management_timezone, now);
  const amsAvail = amsParts ? availabilityFromHour(amsParts.hour) : null;
  const me = data.agent;
  const myParts = me ? getZonedParts(me.timezone, now) : null;
  const myAvail = myParts ? availabilityFromHour(myParts.hour) : null;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="tz-agent-root">
      <header>
        <h1 className="text-3xl font-bold tracking-tight">Timezone Manager</h1>
        <p className="text-sm text-white/40 mt-1">
          Tu zona, tus leads asignados y planificador de reuniones.
        </p>
      </header>

      {/* Sticky top: Gerencia + yo */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div
          data-testid="agent-block-ams"
          className="glass-strong rounded-2xl p-5 border-2"
          style={{ borderColor: (amsAvail?.color || "#64748b") + "60" }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Building2 className="w-3.5 h-3.5 text-lime-300" />
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-lime-300 mono">
              {data.management_city} · Gerencia
            </div>
            <span
              className="ml-auto w-2.5 h-2.5 rounded-full"
              style={{ background: amsAvail?.color || "#64748b", boxShadow: `0 0 10px ${amsAvail?.color || "#64748b"}` }}
            />
          </div>
          <div className="text-4xl font-bold text-white mono">{amsParts?.time || "—"}</div>
          <div className="text-xs text-white/40 mono mt-1">{amsParts?.date}</div>
          <div className="mt-2 text-[10px] px-2 py-0.5 inline-block rounded-full" style={{
            background: (amsAvail?.color || "#64748b") + "20",
            color: amsAvail?.color || "#64748b",
          }}>
            {amsAvail?.label_es}
          </div>
        </div>

        <div
          data-testid="agent-block-me"
          className="glass-strong rounded-2xl p-5 border-2"
          style={{ borderColor: me ? (myAvail?.color || "#64748b") + "60" : "rgba(255,255,255,0.08)" }}
        >
          {me ? (
            <>
              <div className="flex items-center gap-2 mb-2">
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/60 mono">
                  {me.city} · Tu zona
                </div>
                <span
                  className="ml-auto w-2.5 h-2.5 rounded-full"
                  style={{ background: myAvail?.color || "#64748b" }}
                />
              </div>
              <div className="text-4xl font-bold text-white mono">{myParts?.time || "—"}</div>
              <div className="text-xs text-white/40 mono mt-1">
                {myParts?.date} · {offsetFromAmsterdam(me.timezone, data.management_timezone, now)} vs AMS
              </div>
              <div className="mt-2 text-[10px] px-2 py-0.5 inline-block rounded-full" style={{
                background: (myAvail?.color || "#64748b") + "20",
                color: myAvail?.color || "#64748b",
              }}>
                {myAvail?.label_es}
              </div>
            </>
          ) : (
            <div className="text-sm text-white/50 leading-relaxed">
              <div className="font-semibold text-white/80 mb-1">Sin perfil de zona horaria</div>
              Tu admin no ha vinculado aún tu perfil con una zona horaria.
              Pídele que lo configure en <span className="text-lime-300 mono">Timezone Manager → Agentes</span>.
            </div>
          )}
        </div>
      </div>

      {/* Assigned lead zones */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/8 flex items-center justify-center">
            <Globe2 className="w-4 h-4 text-white/70" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Tus zonas de leads</h2>
            <p className="text-xs text-white/40 mono">
              {data.assigned_lead_zones.length} zona(s) asignada(s) por gerencia
            </p>
          </div>
        </div>
        {data.assigned_lead_zones.length === 0 ? (
          <div className="text-center py-8 text-white/40 text-sm bg-white/[0.02] rounded-xl border border-white/5">
            No tienes zonas asignadas aún. Contacta con tu admin.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.assigned_lead_zones.map((z) => (
              <ZoneCard key={z.id} zone={z} now={now} amsTz={data.management_timezone} />
            ))}
          </div>
        )}
      </div>

      {/* Meeting Planner */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/8 flex items-center justify-center">
            <CalendarClock className="w-4 h-4 text-white/70" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Meeting Planner</h2>
            <p className="text-xs text-white/40 mono">
              Calcula ventanas con Gerencia + tu zona + zona de lead.
            </p>
          </div>
        </div>
        <AgentPlanner data={data} now={now} />
      </div>
    </div>
  );
}

function ZoneCard({ zone, now, amsTz }) {
  const parts = getZonedParts(zone.timezone, now);
  const avail = parts ? availabilityFromHour(parts.hour) : null;
  const diff = offsetFromAmsterdam(zone.timezone, amsTz, now);
  return (
    <div
      data-testid={`agent-zone-${zone.id}`}
      className="glass-strong rounded-xl p-4 border-2"
      style={{ borderColor: (avail?.color || "#64748b") + "60" }}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="w-2 h-2 rounded-full" style={{ background: avail?.color }} />
        <div className="font-bold text-white truncate">{zone.name}</div>
      </div>
      <div className="text-xs text-white/50 mono mb-2">{zone.timezone}</div>
      <div className="flex items-baseline justify-between">
        <div className="text-2xl font-bold text-white mono">{parts?.time || "—"}</div>
        <div className="text-xs text-white/50 mono">{diff} vs AMS</div>
      </div>
      <div className="mt-2 text-[10px] px-2 py-0.5 inline-block rounded-full" style={{
        background: (avail?.color || "#64748b") + "20",
        color: avail?.color || "#64748b",
      }}>
        {avail?.label_es}
      </div>
    </div>
  );
}

function AgentPlanner({ data }) {
  const [zoneId, setZoneId] = useState("");
  const zone = data.assigned_lead_zones.find((z) => z.id === zoneId);
  const me = data.agent;

  const tzs = [data.management_timezone];
  if (me) tzs.push(me.timezone);
  if (zone) tzs.push(zone.timezone);

  return (
    <div className="glass-strong rounded-xl p-5 border border-white/8 space-y-4">
      <div>
        <Label className="text-white/70 text-xs">Selecciona una zona asignada</Label>
        <select
          value={zoneId}
          onChange={(e) => setZoneId(e.target.value)}
          data-testid="agent-planner-zone"
          className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-md px-3 py-2 mt-1"
        >
          <option value="">— Selecciona —</option>
          {data.assigned_lead_zones.map((z) => (
            <option key={z.id} value={z.id}>{z.name} ({z.timezone})</option>
          ))}
        </select>
      </div>
      <PlannerResult
        tzs={tzs}
        amsTz={data.management_timezone}
        agent={me ? { name: me.name, timezone: me.timezone } : null}
        zone={zone || null}
      />
    </div>
  );
}
